import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Row, Col, ProgressBar, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

function Dashboard() {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [totalOrders, setTotalOrders] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [avgRating, setAvgRating] = useState(0);

  const [customerSummary, setCustomerSummary] = useState<any>({
    totalCustomers: 0,
    activeCustomers: 0,
    newCustomers30d: 0,
    topCustomers: [],
    recentCustomers: [],
  });

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const [ordersRes, reviewsRes, customersRes] = await Promise.all([
          axios.get('/orders', {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: 1, limit: 100, sort: 'newest' },
          }),
          axios.get('/products/admin/reviews', {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: 1, limit: 1 },
          }),
          axios.get('/customers/admin/activity-summary', {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const orders = Array.isArray(ordersRes.data?.orders) ? ordersRes.data.orders : [];
        setTotalOrders(Number(ordersRes.data?.total ?? orders.length));
        setTotalSales(orders.reduce((sum: number, item: any) => sum + Number(item?.totalAmount ?? 0), 0));

        setTotalReviews(Number(reviewsRes.data?.summary?.totalReviews ?? 0));
        setAvgRating(Number(reviewsRes.data?.summary?.avgRating ?? 0));

        setCustomerSummary(customersRes.data || {
          totalCustomers: 0,
          activeCustomers: 0,
          newCustomers30d: 0,
          topCustomers: [],
          recentCustomers: [],
        });
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load dashboard metrics.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [token]);

  const kpiCards = useMemo(() => [
    { title: 'Sales (Current Page Sample)', value: `Rs. ${totalSales.toFixed(2)}`, note: 'Based on latest loaded orders' },
    { title: 'Total Orders', value: String(totalOrders), note: 'Across order records' },
    { title: 'Active Customers', value: String(customerSummary.activeCustomers || 0), note: `${customerSummary.newCustomers30d || 0} new in 30d` },
    { title: 'Review Health', value: `${avgRating.toFixed(2)} / 5`, note: `${totalReviews} total reviews` },
  ], [totalSales, totalOrders, customerSummary, avgRating, totalReviews]);

  const recentCustomers = Array.isArray(customerSummary?.recentCustomers) ? customerSummary.recentCustomers : [];
  const topCustomers = Array.isArray(customerSummary?.topCustomers) ? customerSummary.topCustomers : [];

  return (
    <>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {loading ? <Alert variant="light">Loading dashboard...</Alert> : null}

      <Row className="g-3 mb-4 admin-kpi-grid">
        {kpiCards.map((card) => (
          <Col md={6} xl={3} key={card.title}>
            <Card className="h-100 border-0 shadow-sm admin-kpi-card">
              <Card.Body>
                <Card.Title className="text-muted fs-6">{card.title}</Card.Title>
                <h3 className="mb-1">{card.value}</h3>
                <small className="text-success">{card.note}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3">
        <Col lg={7}>
          <Card className="border-0 shadow-sm admin-section-card">
            <Card.Body>
              <Card.Title>Top Customer Activity</Card.Title>
              <Table borderless responsive className="mb-0 mt-3">
                <tbody>
                  {topCustomers.length === 0 ? (
                    <tr>
                      <td className="text-muted">No customer activity yet.</td>
                    </tr>
                  ) : topCustomers.map((item: any) => (
                    <tr key={item._id}>
                      <td style={{ width: '180px' }}>{item.name || item.phone}</td>
                      <td style={{ width: '80%' }}>
                        <ProgressBar
                          now={Math.min(100, Number(item.totalOrders || 0) * 10)}
                          label={`${Number(item.totalOrders || 0)} orders`}
                          style={{ height: '18px' }}
                        />
                      </td>
                      <td style={{ width: '120px' }}>Rs. {Number(item.totalSpent || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100 admin-section-card">
            <Card.Body>
              <Card.Title>Recent Customers</Card.Title>
              <div className="mt-3">
                {recentCustomers.length === 0 ? (
                  <div className="text-muted">No recent customer activity.</div>
                ) : recentCustomers.map((customer: any) => (
                  <div className="mb-3" key={customer._id}>
                    <div className="d-flex justify-content-between mb-1">
                      <span>{customer.name || customer.phone}</span>
                      <span>{customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : '-'}</span>
                    </div>
                    <small className="text-muted">{customer.email || customer.phone}</small>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default Dashboard;
